import { Input, InputWrapperProps } from "@mantine/core";
import {
  Editor,
  RichTextEditor as MantineRichTextEditor,
  RichTextEditorProps,
} from "@mantine/rte";
import { User } from "@server/entities";
import Quill from "quill";
import QuillMention from "quill-mention";
import React, { forwardRef, memo, useMemo } from "react";

Quill.register({ "modules/mention": QuillMention }, true);

import { CONTROLS } from "../utils/editor";

import { getInputDefaults } from "./Input";

export type RichTextProps = RichTextEditorProps &
  Omit<InputWrapperProps, "children"> & {
    // Users to include in mentionable array
    users?: User[];
    placeholder?: string;
  };

export const RichText = memo(
  forwardRef<Editor | null, RichTextProps>(({ users, ...props }, ref) => {
    const defaults = getInputDefaults(props);

    const mentions = useMemo(
      () =>
        users
          ? {
              allowedChars: /^[A-Za-z\sÅÄÖåäö]*$/,
              mentionDenotationChars: ["@"],
              showDenotationChar: true,
              source: (searchTerm, renderList) => {
                const includesSearchTerm = users
                  .filter((item) =>
                    item.displayName
                      .toLowerCase()
                      .includes(searchTerm.toLowerCase())
                  )
                  .map((item) => ({
                    id: item.id,
                    value: item.displayName,
                  }));
                renderList(includesSearchTerm);
              },
            }
          : undefined,
      [users?.length]
    );

    return (
      <Input.Wrapper {...defaults}>
        <MantineRichTextEditor
          ref={ref}
          controls={CONTROLS}
          mentions={mentions}
          {...defaults}
          {...props}
          sx={(theme) => ({
            backgroundColor: theme.colors.dark[8],
          })}
          classNames={{
            ...defaults.classNames,
            ...props.classNames,
          }}
        />
      </Input.Wrapper>
    );
  }),
  (prev, next) => prev.value === next.value
);
